import {
  type Awaitable,
  tryOnMounted,
  tryOnUnmounted,
  useElementSize,
  useElementVisibility,
  useScroll,
  useTimeoutFn,
} from "@vueuse/core";
import axios from "axios";
import {
  computed,
  type ComputedRef,
  type MaybeRefOrGetter,
  nextTick,
  reactive,
  type Ref,
  ref,
  toValue,
  watch,
} from "vue";

import { printOrCaptureMessage } from "~/utils/exceptionClient";

type ScrollContainer = HTMLElement | Window | Document | null | undefined;
type ScrollElement = HTMLElement | null | undefined;
type ScrollDirection = "top" | "bottom" | "left" | "right";
type UsePaginatedListOptions = {
  direction?: ScrollDirection;
  ignoreContainerResize?: boolean;
  enabled?: MaybeRefOrGetter<boolean>;
  loadMoreTarget?: MaybeRefOrGetter<ScrollElement>;
};
type PaginatedScrollState = {
  arrivedState: Record<ScrollDirection, boolean>;
  measure: () => void;
};

const LOAD_MORE_DISTANCE_PX = 500;
const DEBOUNCE_TIME_MS = 500;
const STALE_TIME_MS = 1000 * 60 * 5;

const keyCacheMap = reactive(
  new Map<string, { lastLoadTime: number; totalCount: number | null; activePromise?: Promise<number> }>()
);

// Document and Window cannot be observed by IntersectionObserver
const resolveElement = (el: ScrollContainer) => {
  if (typeof Window !== "undefined" && el instanceof Window) {
    return el.document.documentElement;
  }
  if (typeof Document !== "undefined" && el instanceof Document) {
    return el.documentElement;
  }
  return el as ScrollElement;
};

const doesContainerOverflow = (element: ScrollElement, direction: ScrollDirection) => {
  if (!element) {
    return false;
  }

  return direction === "bottom" || direction === "top"
    ? element.scrollHeight > element.clientHeight
    : element.scrollWidth > element.clientWidth;
};

const usePaginatedList = (
  key: Ref<string>,
  scrollContainer: MaybeRefOrGetter<ScrollContainer>,
  alreadyLoadedCount: ComputedRef<number>,
  loadMore: (loadedCount: number) => Awaitable<number>,
  // The amount of spacing after the last item, default matches extra-overscroll
  loadMoreOffset: MaybeRefOrGetter<number> = 300,
  options: UsePaginatedListOptions = {}
) => {
  const { direction = "bottom", ignoreContainerResize = false, enabled = true, loadMoreTarget } = options;
  const state: PaginatedScrollState = reactive(
    useScroll(scrollContainer, {
      offset: { [direction]: toValue(loadMoreOffset) + LOAD_MORE_DISTANCE_PX },
    })
  );
  const keyValue = computed(() => toValue(key));
  const isEnabled = computed(() => toValue(enabled));
  const shouldLoadImmediately = computed(() => direction !== "top");
  const totalCount = ref<number | null>(keyCacheMap.get(keyValue.value)?.totalCount ?? null);

  const isLoading = computed(
    () =>
      !!keyCacheMap.get(keyValue.value)?.activePromise || (totalCount.value === null && alreadyLoadedCount.value === 0)
  );
  const canLoadMore = computed(() => totalCount.value === null || alreadyLoadedCount.value < totalCount.value);

  const observedElement = computed<ScrollElement>(() => resolveElement(toValue(scrollContainer)));
  const isElementVisible = useElementVisibility(observedElement);
  const elementSize = useElementSize(observedElement);

  const isTargetVisible = loadMoreTarget
    ? useElementVisibility(loadMoreTarget, {
        scrollTarget: observedElement,
        rootMargin: `${toValue(loadMoreOffset) + LOAD_MORE_DISTANCE_PX}px`,
      })
    : ref(false);

  if (loadMoreTarget) {
    watch(
      () => toValue(loadMoreTarget),
      () => {
        isTargetVisible.value = false;
      }
    );
  }

  const lastLoadTime = ref<number>(keyCacheMap.get(keyValue.value)?.lastLoadTime ?? 0);
  const { start: retryLoad, stop: cancelRetry } = useTimeoutFn(
    (retry: () => void) => retry(),
    () => DEBOUNCE_TIME_MS - (Date.now() - lastLoadTime.value),
    { immediate: false }
  );
  let isUnmounted = false;

  const checkAndLoad = async (force: boolean = false, respectStaleWindow: boolean = false) => {
    if (isUnmounted) {
      return;
    }
    state.measure();

    const cacheEntry = keyCacheMap.get(keyValue.value);

    if (cacheEntry?.activePromise) {
      const loadKey = keyValue.value;
      try {
        const result = await cacheEntry.activePromise;
        if (loadKey === keyValue.value) {
          totalCount.value = result;
        }
      } catch {
        if (loadKey === keyValue.value) {
          totalCount.value = totalCount.value ?? alreadyLoadedCount.value;
        }
      }
      return;
    }

    if (!isEnabled.value || !canLoadMore.value || (!force && (!observedElement.value || !isElementVisible.value))) {
      return;
    }

    const isWithinContainer = observedElement.value ? !doesContainerOverflow(observedElement.value, direction) : false;

    const isNearEnd = loadMoreTarget ? isTargetVisible.value : state.arrivedState[direction] || isWithinContainer;
    if (isNearEnd || force) {
      // Debounce the load to prevent rapid successive loads
      const now = Date.now();
      if (!force && now - lastLoadTime.value < DEBOUNCE_TIME_MS) {
        retryLoad(checkAndLoad);
        return;
      }

      // Check if we're within stale time for forced loads
      const keyLastLoadTime = cacheEntry?.lastLoadTime;
      if (force && respectStaleWindow && keyLastLoadTime && now - keyLastLoadTime < STALE_TIME_MS) {
        return;
      }

      const previousTotalCount = totalCount.value;
      const previousLoadedCount = alreadyLoadedCount.value;
      const loadKey = keyValue.value;

      try {
        lastLoadTime.value = now;

        const topScrollElement = direction === "top" ? observedElement.value : null;
        const topScrollHeight = topScrollElement?.scrollHeight;
        const activePromise = Promise.resolve(loadMore(alreadyLoadedCount.value));
        keyCacheMap.set(keyValue.value, {
          lastLoadTime: now,
          totalCount: cacheEntry?.totalCount ?? totalCount.value,
          activePromise,
        });
        const result = await activePromise;
        if (loadKey !== keyValue.value) {
          return;
        }
        if (topScrollElement && topScrollElement === observedElement.value && topScrollHeight !== undefined) {
          await nextTick();
          topScrollElement.scrollTop += topScrollElement.scrollHeight - topScrollHeight;
          state.measure();
        }

        totalCount.value = result;
        const cacheEntryToUpdate = keyCacheMap.get(keyValue.value);
        if (cacheEntryToUpdate) {
          cacheEntryToUpdate.totalCount = result;
          delete cacheEntryToUpdate.activePromise;
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
        if (loadKey === keyValue.value) {
          totalCount.value = totalCount.value ?? alreadyLoadedCount.value;
        }
      } finally {
        const currentCacheEntry = keyCacheMap.get(loadKey);
        if (currentCacheEntry?.activePromise) {
          delete currentCacheEntry.activePromise;
        }

        // Load more as needed, which happens when the current batch doesn't fill the element
        if (alreadyLoadedCount.value !== previousLoadedCount || totalCount.value !== previousTotalCount) {
          nextTick(() =>
            checkAndLoad(
              !loadMoreTarget && !!observedElement.value && !doesContainerOverflow(observedElement.value, direction),
              false
            )
          );
        } else {
          printOrCaptureMessage("Attempted to load more items but total count didn't change");
        }
      }
    }
  };

  const forceCheckAndLoad = () => checkAndLoad(true, true);

  // Reset the state
  const reset = () => {
    cancelRetry();
    keyCacheMap.delete(keyValue.value);
    totalCount.value = null;
    lastLoadTime.value = 0;
    if (shouldLoadImmediately.value) {
      checkAndLoad(true);
    }
  };

  // Initial load
  tryOnMounted(() => {
    if (shouldLoadImmediately.value) {
      forceCheckAndLoad();
    }
  });
  watch(isEnabled, (enabledNow) => {
    if (enabledNow && shouldLoadImmediately.value) {
      forceCheckAndLoad();
    }
  });

  // Watch for changes
  const stop = watch(
    // elementSize.width.value needs to be added here if we implement horizontal infinite scroll
    // but right now it causes issues with vertical infinite scroll when the vertical scrollbar appears/disappears
    [
      () => state.arrivedState[direction],
      isElementVisible,
      isTargetVisible,
      isEnabled,
      () => window.innerHeight,
      () => (ignoreContainerResize ? undefined : elementSize.height.value),
    ],
    () => checkAndLoad()
  );
  tryOnUnmounted(() => {
    isUnmounted = true;
    stop();
  });

  return { isLoading, totalCount, canLoadMore, reset, loadMore: forceCheckAndLoad };
};

export default usePaginatedList;
